import { Handshake, LayoutDashboard } from "lucide-react";
import { lazy } from "react";

import { DealCaptureExplore } from "@/components/deal-capture/sidebar/DealCaptureExplore";
import { DealCaptureSidebarHeader } from "@/components/deal-capture/sidebar/DealCaptureSidebarHeader";
import {
  AREAS,
  DC_PATH,
  NEW_DEAL_PATH,
  areaListPath,
  areaPath,
} from "@/lib/deal-capture/paths";

import type { ModuleDefinition } from "@/lib/module-registry";
import type { AppRoute } from "@/lib/routes";
import type { DealCaptureArea } from "@/types/deal-capture";

/**
 * Deal Capture (AB#8984): supply deals, master contracts and legal entities.
 *
 * **Behind the `deal_capture` tenant flag, on every route.** The landing too:
 * the flag gates the whole module, so with it off the module is absent from
 * the switcher and every url 404s through `ProtectedRoute`. Access within a
 * tenant is `deal_capture.access`.
 *
 * **The sidebar is the url.** The Area picker, the Integrated / Non-Integrated
 * toggle and the Deal / Contract / Entity pickers (`sidebarHeader`), and the
 * Explore tree (`sidebarNav`), all read the location (`lib/deal-capture/
 * paths.ts`). The module switcher links to `/deal-capture`, which has no area
 * and no record, so choosing Deal Capture clears every selection and opens the
 * landing without any state to reset.
 *
 * **Most pages are placeholders** until the screens arrive (AB#8985–8988):
 * they hold their route, breadcrumbs and sidebar state, and the deal and
 * contract pages carry the sections the sidebar's jump links scroll to.
 */
const DealCaptureLandingPage = lazy(() =>
  import("@/pages/deal-capture/DealCaptureLandingPage").then((m) => ({
    default: m.DealCaptureLandingPage,
  })),
);
const DealCaptureAreaPage = lazy(() =>
  import("@/pages/deal-capture/DealCaptureAreaPage").then((m) => ({
    default: m.DealCaptureAreaPage,
  })),
);
const DealCaptureRecordPage = lazy(() =>
  import("@/pages/deal-capture/DealCaptureRecordPage").then((m) => ({
    default: m.DealCaptureRecordPage,
  })),
);

const FEATURE = "deal_capture" as const;

function crumbOf(area: DealCaptureArea): string {
  return AREAS.find((a) => a.key === area)?.crumb ?? area;
}

/** An area's Summary and its list, with the spec's breadcrumb trails. */
function areaRoutes(area: DealCaptureArea): AppRoute[] {
  const root = { label: crumbOf(area), href: areaListPath(area) };
  const listTitle = area === "deleted" ? "All deleted deals" : `All ${area}`;
  return [
    {
      path: areaPath(area),
      label: `${crumbOf(area)} summary`,
      status: "live",
      requiredFeature: FEATURE,
      element: <DealCaptureAreaPage area={area} view="summary" />,
      breadcrumbs: [root, { label: "Summary" }],
    },
    {
      path: areaListPath(area),
      label: listTitle,
      status: "live",
      requiredFeature: FEATURE,
      element: <DealCaptureAreaPage area={area} view="list" />,
      breadcrumbs: [root, { label: "Summary", href: areaPath(area) }, { label: listTitle }],
    },
  ];
}

/** A record page; its trail is set by the page, which knows the record's name. */
function recordRoute(area: "deals" | "contracts" | "entities", label: string): AppRoute {
  return {
    path: `${areaPath(area)}/:id`,
    label,
    status: "live",
    requiredFeature: FEATURE,
    element: <DealCaptureRecordPage area={area} />,
  };
}

const hiddenRoutes: AppRoute[] = [
  ...AREAS.flatMap((a) => areaRoutes(a.key)),
  // Before `/deals/:id`, which would otherwise read "new" as a deal number.
  {
    path: NEW_DEAL_PATH,
    label: "New deal",
    status: "live",
    requiredFeature: FEATURE,
    element: <DealCaptureAreaPage area="deals" view="new" />,
    breadcrumbs: [
      { label: "Deals", href: areaListPath("deals") },
      { label: "Summary", href: areaPath("deals") },
      { label: "New deal" },
    ],
  },
  recordRoute("deals", "Deal"),
  recordRoute("contracts", "Contract"),
  recordRoute("entities", "Entity"),
];

export const DEAL_CAPTURE_MODULE: ModuleDefinition = {
  key: "deal-capture",
  label: "Deal Capture",
  icon: Handshake,
  // `description` omitted — inherited from the launcher tile.
  entryPath: DC_PATH,
  requiredPermission: "deal_capture.access",
  sidebarHeader: ({ onClose }) => <DealCaptureSidebarHeader onClose={onClose} />,
  sidebarNav: DealCaptureExplore,
  sections: [
    {
      key: "deal-capture-explore",
      label: "Explore",
      routes: [
        {
          path: DC_PATH,
          label: "Overview",
          icon: LayoutDashboard,
          status: "live",
          requiredFeature: FEATURE,
          element: <DealCaptureLandingPage />,
          title: "Deal Capture",
          breadcrumbs: [{ label: "Deal Capture", href: DC_PATH }, { label: "Overview" }],
        },
      ],
    },
  ],
  hiddenRoutes,
};
